import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { StatusBadge } from '../components/StatusBadge.js';
import { Callout } from '../ui/Callout.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useAuth } from '../auth/useAuth.js';
import { useDomain, useEntry } from '../domains/queries.js';
import { WeightEditor } from '../weights/WeightEditor.js';
import { urlsListPath } from './listLocation.js';
import { displayUrl } from './parseUrl.js';
import { useNavigationGuard } from './useNavigationGuard.js';

/**
 * The scheme `displayUrl` prepends, on its own so the heading can mute it.
 *
 * Derived rather than written out: the scheme is a display convention, nothing
 * in the schema records one, and `parseUrl` is where that convention lives.
 */
const DISPLAY_SCHEME = displayUrl('', '');

type GuardedLinkProps = {
  to: string;
  onRequest: (to: string) => void;
  className?: string;
  children: ReactNode;
};

/**
 * A real link that asks the guard before it is followed.
 *
 * It stays an `<a>` with an honest `href`, so it still reads as a link, still
 * shows its target in the status bar, and a middle- or modifier-click still
 * opens it in a new tab — none of which can abandon a draft, because none of
 * them leave this page.
 */
function GuardedLink({ to, onRequest, className, children }: GuardedLinkProps) {
  return (
    <Link
      to={to}
      className={className}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;
        event.preventDefault();
        onRequest(to);
      }}
    >
      {children}
    </Link>
  );
}

/**
 * One URL: what it is, and where its traffic goes.
 *
 * The heading is the URL, plain. It used to hang a link to the domain settings
 * page off the hostname, which made the one piece of text on this page you are
 * most likely to select and copy into something that navigates away when you
 * click it — and put a link in the middle of a value rather than beside it.
 *
 * The page carries no action toolbar either. Copy, Test, Edit, Clone, Delete
 * and the status menu all live on the URLs listing now, on the row for each
 * URL: that is where an operator scanning eight hundred URLs needs them, and
 * having them in two places meant every one of them had to be built, labelled
 * and kept working twice.
 */
export function UrlScreen() {
  const params = useParams();
  const entryId = Number(params.entryId ?? params.id);
  const { user } = useAuth();
  const canEdit = user?.role === 'admin';

  const detail = useEntry(entryId);
  const entry = detail.data?.entry;
  const domainId = detail.data?.domain.id ?? 0;
  const hostname = detail.data?.domain.hostname ?? '';
  const domainDisabled = detail.data?.domain.enabled === false;

  // Only for the breadcrumb: the entry detail carries a group id but not its
  // name, and a breadcrumb reading "← 4" would be worse than a second request.
  const domain = useDomain(domainId, domainId !== 0);

  /*
   * The draft itself stays inside the weight editor, which is the only thing
   * that can own it; this page is told only whether one is at stake. That is
   * the same contract the accordion used, so nothing about the draft can
   * outlive the editor that holds it.
   */
  const [dirty, setDirty] = useState(false);
  const guard = useNavigationGuard(dirty);
  const handleDirtyChange = useCallback((next: boolean) => setDirty(next), []);

  const fullUrl = displayUrl(hostname, entry?.path ?? '');

  if (detail.isError) {
    return (
      <section aria-labelledby="url-heading" className="flex flex-col gap-(--space-5)">
        <h1 id="url-heading" className="text-[length:var(--text-title)]">
          URL
        </h1>
        <Callout tone="danger">
          {detail.error instanceof Error ? detail.error.message : 'Could not load this URL'}
        </Callout>
      </section>
    );
  }

  return (
    <section aria-labelledby="url-heading" className="flex flex-col gap-(--space-5)">
      <header className="flex flex-wrap items-start justify-between gap-(--space-4)">
        <div className="min-w-0">
          {/* Back to the listing, not up to the group.
              The URL page is reached from the URLs listing, so back is that
              listing — and with the group and search filter the operator had
              chosen, which `urlsListPath` remembers for the session. The group
              is still one click away: it names this URL just below. */}
          <GuardedLink
            to={urlsListPath()}
            onRequest={guard.request}
            /* Not uppercased, unlike the eyebrow labels elsewhere: this one
               is a product name with its own casing, and `text-transform`
               turned "URLs" into "URLS". */
            className="text-[length:var(--text-small)] font-medium text-ink-subtle underline-offset-4 hover:underline"
          >
            ← URLs
          </GuardedLink>

          {/* The URL is the data on this page, so it is the heading, and it is
              set in the same monospace the operator will paste it into. Plain
              text throughout: it is a value to be read and copied, not a
              control. */}
          <h1
            id="url-heading"
            className="mt-(--space-1) break-all font-mono text-[length:var(--text-title)] font-bold text-ink"
          >
            {entry === undefined ? (
              '…'
            ) : (
              <>
                <span className="text-ink-subtle">{DISPLAY_SCHEME}</span>
                <span>{hostname}</span>
                <span>{entry.path}</span>
              </>
            )}
          </h1>

          {/* Rendered only when there is one. Most entries carry no label, and
              a muted "No label" on every page would be noise. */}
          {entry !== undefined && entry.label !== '' && (
            <p className="mt-(--space-2) text-[length:var(--text-small)] text-ink-muted">
              {entry.label}
            </p>
          )}

          {/* The group, now that the breadcrumb points at the listing instead.
              A URL belongs to a client, and that is worth saying on the page
              that configures where its traffic goes. */}
          {domain.data !== undefined && (
            <p className="mt-(--space-2) text-[length:var(--text-small)] text-ink-muted">
              <GuardedLink
                to={`/urls?group=${domain.data.group.id}`}
                onRequest={guard.request}
                className="underline-offset-4 hover:underline"
              >
                {domain.data.group.name}
              </GuardedLink>
            </p>
          )}

          {/* The consequence of the flags, in prose that belongs with the URL
              it is about. */}
          {entry !== undefined && (!entry.enabled || domainDisabled) && (
            <div className="mt-(--space-3) flex flex-col gap-(--space-1)">
              {!entry.enabled && (
                <p className="text-[length:var(--text-small)] text-warn">
                  This URL is disabled. Traffic arriving on it falls through to the fallback.
                </p>
              )}
              {domainDisabled && (
                <p className="text-[length:var(--text-small)] text-warn">
                  Its domain is disabled, so nothing on this hostname redirects.
                </p>
              )}
            </div>
          )}
        </div>

        {/* A badge, not a menu. The status is a fact worth seeing on this page;
            SETTING it is one of the quick actions, and those now live on the
            listing where every URL has one. */}
        {entry !== undefined && (
          <StatusBadge
            status={entry.enabled ? 'active' : 'paused'}
            label={entry.enabled ? 'Enabled' : 'Disabled'}
          />
        )}
      </header>

      {/* The editor owns its own heading and its own toolbar: the actions
          belong beside that heading, and a wrapper here would put a second
          one above it. */}
      {detail.isLoading && (
        <p role="status" className="text-ink-subtle">
          Loading targets…
        </p>
      )}

      {detail.data !== undefined && (
        <WeightEditor
          entryId={entryId}
          destinations={detail.data.destinations}
          canEdit={canEdit}
          onDirtyChange={handleDirtyChange}
        />
      )}

      <ConfirmDialog
        open={guard.pending !== null}
        title="Discard unsaved split edits?"
        body={`The destination split you edited for ${fullUrl} has not been saved. Leaving this page discards those percentages and URLs; live traffic keeps following the split that is saved now.`}
        confirmLabel="Discard split edits"
        onClose={guard.cancel}
        onConfirm={guard.confirm}
      />
    </section>
  );
}
